import { motion, AnimatePresence } from "framer-motion";
import { Check, AlertTriangle, MinusCircle } from "lucide-react";
import { cn } from "../../lib/cn";
import type { PlanEvent, PlanItem } from "../../lib/types";

/**
 * Вертикальный таймлайн плана задачи: connecting line слева, узлы для каждого шага,
 * pulsing-кольцо вокруг активного узла, прогресс-бар сверху.
 * Не меняет логику store — принимает PlanEvent как раньше.
 */
function scrollToStep(index: number) {
  const el = document.getElementById(`step-report-${index}`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function PlanPanel({ plan }: { plan: PlanEvent | null }) {
  if (!plan || plan.items.length === 0) return null;
  const completed = plan.completed;
  const total = plan.total;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <motion.aside
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.22 }}
      className="card p-3 lg:p-4 space-y-3 sticky top-4 max-w-full"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] font-mono uppercase tracking-wider text-fg-subtle">
          План задачи
        </div>
        <span className="text-small font-medium text-fg">
          {completed}/{total}
          <span className="text-fg-subtle font-mono ml-1">· {percent}%</span>
        </span>
      </div>

      {/* Premium progress bar */}
      <div className="relative h-1.5 rounded-full bg-bg-subtle overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-grad-cta"
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
        {percent > 0 && percent < 100 && (
          <motion.span
            className="absolute inset-y-0 w-12 rounded-full opacity-60 mix-blend-overlay"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgb(255 255 255 / 0.6) 50%, transparent 100%)",
            }}
            animate={{ x: ["-100%", `${percent * 8}%`] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
      </div>

      {/* Vertical timeline */}
      <ol className="relative pt-1">
        {/* Connecting line */}
        <span
          aria-hidden
          className="absolute left-[15px] top-1 bottom-1 w-px bg-border/70"
        />
        <AnimatePresence initial={false}>
          {plan.items.map((it) => (
            <motion.li
              key={it.index}
              layout
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => scrollToStep(it.index)}
              className={cn(
                "relative flex items-start gap-3 px-1 py-2 rounded-lg cursor-pointer hover:bg-bg-subtle/80 transition-colors",
                it.status === "active" && "bg-bg-subtle/60",
              )}
            >
              <TimelineNode status={it.status} />
              <div className="flex-1 min-w-0 pt-0.5">
                <div
                  className={cn(
                    "text-small leading-snug break-words",
                    it.status === "done" && "text-fg-muted line-through opacity-80",
                    it.status === "active" && "text-fg font-medium",
                    it.status === "pending" && "text-fg-muted",
                  )}
                >
                  <span className="text-fg-subtle font-mono mr-1.5">{it.index}.</span>
                  {it.title}
                </div>
                {(it.report || it.narration) && (
                  <div className="mt-1 text-[12px] text-fg-subtle line-clamp-3 italic break-words">
                    {it.report || it.narration}
                  </div>
                )}
              </div>
            </motion.li>
          ))}
        </AnimatePresence>
      </ol>
    </motion.aside>
  );
}

function TimelineNode({ status }: { status: PlanItem["status"] }) {
  const base = "relative grid place-items-center size-7 rounded-full border-2 shrink-0 bg-bg-card";
  if (status === "done") {
    return (
      <span className={cn(base, "border-success/50 text-success")}>
        <Check className="size-3.5" />
      </span>
    );
  }
  if (status === "active") {
    return (
      <span className={cn(base, "border-accent-500 text-accent-500 shadow-glow")}>
        {/* Pulsing ring */}
        <span className="absolute inset-0 rounded-full bg-accent-500/30 animate-pulse-ring" />
        <span className="relative size-2 rounded-full bg-accent-500 shadow-accent" />
      </span>
    );
  }
  if (status === "partial") {
    return (
      <span className={cn(base, "border-warning/50 text-warning")}>
        <AlertTriangle className="size-3.5" />
      </span>
    );
  }
  if (status === "skipped") {
    return (
      <span className={cn(base, "border-border text-fg-subtle opacity-60")}>
        <MinusCircle className="size-3.5" />
      </span>
    );
  }
  // pending
  return (
    <span className={cn(base, "border-border text-fg-subtle")}>
      <span className="size-1.5 rounded-full bg-fg-subtle" />
    </span>
  );
}
